import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import { Colors, Typography } from '../../theme';
import { useI18n } from '../../i18n';
import TonyteModal from '../../components/TonyteModal';
import { checkVibeRecoveryCode } from './vibeClaimApi';
import { normalizeRecoveryCode } from './vibeClaimLinks';
import {
  clearPendingVibeClaim,
  loadPendingVibeClaim,
  savePendingVibeClaim,
  subscribePendingVibeClaim,
  type PendingVibeClaim,
} from './pendingVibeClaim';

// "Played on the web?" — recovery-code entry for web Vibe game answers. Works
// signed out (Login screen: the code is kept through sign-up/onboarding) and
// signed in (Vibes tab: VibeClaimCoordinator claims it right away).
//
// One code per quiz day: on the Vibes tab the caller passes today's Day Vibes
// state. Once the account has played today (in the app, or a claimed web code)
// the button explains that instead of opening the code box; while an entered
// code is still being added it says so; before the round opens (06:00) it
// says when. The server enforces the same rule (a second set of answers for
// the same day is never accepted — claim outcome `already_completed`).

export type VibeCodeDayState = 'unknown' | 'not_open' | 'available' | 'played';

type Props = {
  signedIn: boolean;
  style?: object;
  variant?: 'link' | 'vibes';
  /** Today's round for this account (Vibes tab). Omitted on the Login screen. */
  dayState?: VibeCodeDayState;
  onSeeVibes?: () => void;
};

type Notice = 'played' | 'adding' | 'not_open' | null;

export function VibeCodeEntryLink({ signedIn, style, variant = 'link', dayState = 'available', onSeeVibes }: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [pending, setPending] = useState<PendingVibeClaim | null>(null);
  const isVibesCta = variant === 'vibes';

  useEffect(() => {
    let mounted = true;
    loadPendingVibeClaim().then(value => {
      if (mounted) setPending(value);
    });
    const unsubscribe = subscribePendingVibeClaim(setPending);
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const onPress = () => {
    if (dayState === 'unknown') return; // today's status still loading
    if (dayState === 'played') return setNotice('played');
    if (signedIn && pending) return setNotice('adding');
    if (dayState === 'not_open') return setNotice('not_open');
    setOpen(true);
  };

  const closeNotice = () => setNotice(null);
  const noticeContent = notice === 'played'
    ? {
        title: t('vibeClaim.alreadyPlayedTodayTitle'),
        message: t('vibeClaim.alreadyPlayedTodayBody'),
        icon: 'check-circle',
        buttons: onSeeVibes
          ? [
              { label: t('vibeClaim.ok'), variant: 'secondary' as const, onPress: closeNotice },
              { label: t('vibeClaim.seeMyVibes'), variant: 'primary' as const, onPress: () => { closeNotice(); onSeeVibes(); } },
            ]
          : [{ label: t('vibeClaim.ok'), variant: 'primary' as const, onPress: closeNotice }],
      }
    : notice === 'adding'
      ? { title: t('vibeClaim.addingTitle'), message: t('vibeClaim.addingBody'), icon: 'loader', buttons: [{ label: t('vibeClaim.ok'), variant: 'primary' as const, onPress: closeNotice }] }
      : notice === 'not_open'
        ? { title: t('vibeClaim.notOpenTitle'), message: t('vibeClaim.notOpenBody'), icon: 'clock', buttons: [{ label: t('vibeClaim.ok'), variant: 'primary' as const, onPress: closeNotice }] }
        : null;

  return (
    <>
      <TouchableOpacity
        onPress={onPress}
        disabled={dayState === 'unknown'}
        style={[styles.link, isVibesCta && styles.vibesCta, style]}
        accessibilityRole="button"
        accessibilityLabel={t('vibeClaim.playedOnWeb')}
        activeOpacity={0.78}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      >
        {!isVibesCta && <Icon name="globe" size={13} color={Colors.turquoise} />}
        <Text style={[styles.linkText, isVibesCta && styles.vibesCtaText]}>{t('vibeClaim.playedOnWeb')}</Text>
        {isVibesCta && (
          <View style={styles.vibesCtaArrow}>
            <Icon name="arrow-right" size={19} color="#FFFFFF" />
          </View>
        )}
      </TouchableOpacity>
      <VibeCodeEntryModal visible={open} signedIn={signedIn} onClose={() => setOpen(false)} />
      <TonyteModal
        visible={Boolean(noticeContent)}
        title={noticeContent?.title || ''}
        message={noticeContent?.message}
        icon={noticeContent?.icon}
        buttons={noticeContent?.buttons || []}
        onDismiss={closeNotice}
      />
    </>
  );
}

type Status = 'idle' | 'checking' | 'error' | 'saved';

export function VibeCodeEntryModal({ visible, signedIn, onClose }: { visible: boolean; signedIn: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const [value, setValue] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (visible) {
      setValue('');
      setStatus('idle');
      setMessage('');
    }
  }, [visible]);

  const fail = (key: string) => {
    setStatus('error');
    setMessage(t(key));
  };

  const submit = async () => {
    const code = normalizeRecoveryCode(value);
    if (!code) {
      fail('vibeClaim.invalidFormat');
      return;
    }
    setStatus('checking');
    setMessage('');
    try {
      const check = await checkVibeRecoveryCode(code);
      if (!check.valid) return fail('vibeClaim.invalidCode');
      if (!check.roundIsCurrent) return fail('vibeClaim.oldRoundCode');
      // Signed in: let the claim itself decide (same account → idempotent
      // replay; another account → explained). Signed out: say so now.
      if (check.claimStatus === 'claimed' && !signedIn) return fail('vibeClaim.alreadyClaimedCode');
      await savePendingVibeClaim({ code }, 'code_entry');
      if (signedIn) {
        onClose();
        return;
      }
      setStatus('saved');
      setMessage(t('vibeClaim.savedSignIn'));
    } catch (error) {
      const reason = (error as { reason?: string }).reason;
      fail(reason === 'rate_limited' ? 'vibeClaim.rateLimited' : 'vibeClaim.networkError');
    }
  };

  const checking = status === 'checking';
  const saved = status === 'saved';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={styles.backdropWrap}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityLabel={t('common.cancel')} />
        <View style={styles.card} accessibilityViewIsModal>
          <LinearGradient
            colors={['rgba(27,45,78,0.97)', 'rgba(10,16,36,0.98)', 'rgba(28,14,30,0.97)']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
          <View style={styles.cardGlowCyan} />
          <View style={styles.cardGlowOrange} />
          <LinearGradient
            colors={['#43E7F4', '#AE74FF', '#FF9A46']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.cardAccent}
          />
          <View style={styles.introGlass}>
            <View style={styles.modalHeader}>
              <Text style={styles.title} accessibilityRole="header">{t('vibeClaim.enterCodeTitle')}</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={onClose}
                accessibilityRole="button"
                accessibilityLabel={t('common.cancel')}
              >
                <Icon name="x" size={19} color="rgba(255,255,255,0.78)" />
              </TouchableOpacity>
            </View>
            <Text style={styles.body}>{t('vibeClaim.enterCodeBody')}</Text>
          </View>
          {saved && (
            <View style={styles.savedIcon}>
              <Icon name="check" size={22} color="#FFFFFF" />
            </View>
          )}
          {!saved && (
            <TextInput
              value={value}
              onChangeText={text => {
                setValue(text.toUpperCase());
                if (status === 'error') setStatus('idle');
              }}
              placeholder="ABCD-EFGH"
              placeholderTextColor="rgba(255,255,255,0.3)"
              autoCapitalize="characters"
              autoCorrect={false}
              autoComplete="off"
              maxLength={9}
              editable={!checking}
              returnKeyType="done"
              onSubmitEditing={submit}
              style={styles.input}
              accessibilityLabel={t('vibeClaim.enterCodeTitle')}
            />
          )}
          {message ? (
            <Text style={[styles.message, status === 'error' ? styles.messageError : styles.messageOk]} accessibilityLiveRegion="polite">
              {message}
            </Text>
          ) : null}
          <View style={styles.buttons}>
            {saved ? (
              <TouchableOpacity style={[styles.button, styles.primary]} onPress={onClose} accessibilityRole="button">
                <Text style={styles.primaryText}>{t('vibeClaim.ok')}</Text>
              </TouchableOpacity>
            ) : (
              <>
                <TouchableOpacity style={[styles.button, styles.secondary]} onPress={onClose} accessibilityRole="button">
                  <Text style={styles.secondaryText}>{t('common.cancel')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.button, styles.primary, checking && styles.disabled]}
                  onPress={submit}
                  disabled={checking}
                  accessibilityRole="button"
                  accessibilityState={{ busy: checking, disabled: checking }}
                >
                  {checking ? <ActivityIndicator color="#fff" /> : <Text style={styles.primaryText}>{t('vibeClaim.continue')}</Text>}
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}

/** Login screen: "your web answers are waiting" + explicit dismissal. */
export function PendingVibeClaimBanner() {
  const { t } = useI18n();
  const [pending, setPending] = useState<PendingVibeClaim | null>(null);

  useEffect(() => {
    let mounted = true;
    loadPendingVibeClaim().then(value => {
      if (mounted) setPending(value);
    });
    const unsubscribe = subscribePendingVibeClaim(setPending);
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  if (!pending) return null;
  return (
    <View style={styles.banner} accessibilityRole="summary">
      <Icon name="zap" size={16} color={Colors.turquoise} />
      <Text style={styles.bannerText}>{t('vibeClaim.waitingBanner')}</Text>
      <TouchableOpacity
        onPress={() => clearPendingVibeClaim(pending.credential)}
        accessibilityRole="button"
        accessibilityLabel={t('vibeClaim.discard')}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      >
        <Icon name="x" size={16} color={Colors.textSecondary} />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  link: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    minHeight: 44,
    paddingVertical: 8,
  },
  linkText: {
    color: Colors.turquoise,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  vibesCta: {
    alignSelf: 'center',
    width: '100%',
    minHeight: 58,
    justifyContent: 'flex-start',
    gap: 10,
    paddingVertical: 7,
    paddingLeft: 22,
    paddingRight: 7,
    borderRadius: 29,
    backgroundColor: '#FFF6EA',
    shadowColor: '#000',
    shadowOpacity: 0.28,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 7 },
    elevation: 5,
  },
  vibesCtaText: {
    flex: 1,
    color: '#1C140E',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  vibesCtaArrow: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F27D26',
  },
  backdropWrap: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 22,
    backgroundColor: 'rgba(2,4,12,0.78)',
  },
  card: {
    width: '100%',
    maxWidth: 440,
    alignSelf: 'center',
    borderRadius: 30,
    paddingHorizontal: 22,
    paddingTop: 24,
    paddingBottom: 20,
    gap: 12,
    overflow: 'hidden',
    backgroundColor: 'rgba(10,16,36,0.96)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    shadowColor: '#26D0CE',
    shadowOpacity: 0.2,
    shadowRadius: 28,
    shadowOffset: { width: 0, height: 12 },
    elevation: 16,
  },
  cardAccent: {
    position: 'absolute',
    top: 0,
    left: 30,
    right: 30,
    height: 4,
    borderBottomLeftRadius: 3,
    borderBottomRightRadius: 3,
  },
  cardGlowCyan: {
    position: 'absolute',
    left: -70,
    top: -70,
    width: 190,
    height: 190,
    borderRadius: 95,
    backgroundColor: 'rgba(38,208,206,0.12)',
  },
  cardGlowOrange: {
    position: 'absolute',
    right: -80,
    bottom: -90,
    width: 210,
    height: 210,
    borderRadius: 105,
    backgroundColor: 'rgba(242,125,38,0.13)',
  },
  introGlass: {
    gap: 10,
    padding: 16,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.16)',
    backgroundColor: 'rgba(255,255,255,0.055)',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(255,255,255,0.08)',
  },
  savedIcon: {
    alignSelf: 'center',
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F27D26',
    marginVertical: 4,
  },
  title: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.extrabold,
  },
  body: {
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    lineHeight: 20,
    marginBottom: 8,
  },
  input: {
    alignSelf: 'stretch',
    minHeight: 58,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    backgroundColor: 'rgba(18,25,45,0.78)',
    color: '#FFF6EA',
    fontSize: 22,
    fontWeight: Typography.bold,
    letterSpacing: 4.5,
    textAlign: 'center',
    fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', default: undefined }),
  },
  message: {
    fontSize: Typography.sm,
    lineHeight: 19,
    textAlign: 'left',
  },
  messageError: { color: '#FCA5A5' },
  messageOk: { color: '#99F6E4' },
  buttons: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    gap: 10,
    marginTop: 8,
  },
  button: {
    flex: 1,
    minHeight: 50,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primary: {
    backgroundColor: '#F27D26',
    borderWidth: 1,
    borderColor: 'rgba(255,190,123,0.72)',
    shadowColor: '#F27D26',
    shadowOpacity: 0.32,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 7 },
    elevation: 5,
  },
  secondary: {
    backgroundColor: 'rgba(255,255,255,0.075)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
  },
  disabled: { opacity: 0.6 },
  primaryText: { color: '#fff', fontSize: Typography.base, fontWeight: Typography.bold },
  secondaryText: { color: 'rgba(255,255,255,0.7)', fontSize: Typography.base, fontWeight: Typography.semibold },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.32)',
    backgroundColor: 'rgba(38,208,206,0.1)',
    marginBottom: 12,
  },
  bannerText: {
    flex: 1,
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    lineHeight: 19,
    fontWeight: Typography.semibold,
  },
});
